"use client";

import { Spinner } from "@/components/ui/spinner";

import { useImperativeHandle, useState, useSyncExternalStore, type Ref } from "react";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { ClipboardList, RefreshCw, ChevronLeft, ChevronRight, X } from "lucide-react";
import { TaskCard } from "@/components/task-card";
import { TaskCardSkeleton } from "@/components/skeletons/task-card-skeleton";
import { CamundaUserService} from "@/lib/camunda";
import { useTaskListTitulos } from "@/hooks/use-task-list-titulos";
import { 
  useTaskList, 
  useTaskInitialLoad,
  useTaskListRefreshing,
  useTaskError, 
  useLoadTasks,
  useRealtimeConnected,
  useRealtimeError,
} from "@/stores/task-store";

import { cn } from "@/lib/utils";
import {
  getTaskModeState,
  setTaskModeState,
  subscribeTaskModeState,
} from '@/lib/user-preferences';
import { isTaskVisibleToCurrentUser } from './task-visibility';

interface TasksSidePanelRef {
  loadTasks: () => void;
}

interface TasksSidePanelProps {
  onTaskClick?: (taskId: string) => void;
  onTaskPrefetch?: (taskId: string) => void;
  mobile?: boolean;
  onMobileClose?: () => void;
  ref?: Ref<TasksSidePanelRef | null>;
}

const handleTaskModeChange = (checked: boolean) => {
  setTaskModeState(checked);
};

export function TasksSidePanel({
    onTaskClick,
    onTaskPrefetch,
    mobile = false,
    onMobileClose,
    ref
}: TasksSidePanelProps) {
    const [isCollapsed, setIsCollapsed] = useState(false);
    const showAllTasks = useSyncExternalStore(
      subscribeTaskModeState,
      getTaskModeState,
      () => false,
    );
    const isPanelCollapsed = !mobile && isCollapsed;
    const currentUserId = CamundaUserService.getCurrentUserId();
    
    const tasks = useTaskList();
    const isInitialLoad = useTaskInitialLoad();
    const isTaskListRefreshing = useTaskListRefreshing();
    const error = useTaskError();
    const loadTasks = useLoadTasks();

    const isConnected = useRealtimeConnected();
    const realtimeError = useRealtimeError();

    useImperativeHandle(ref, () => ({
      loadTasks,
    }), [loadTasks]);
    
    const filteredTasks = tasks.filter((task) => (
      showAllTasks || isTaskVisibleToCurrentUser(task, currentUserId)
    ));

    const { titulosByProcessInstanceId } = useTaskListTitulos(filteredTasks);

    const expandCollapsedPanel = () => setIsCollapsed(false);
    const hasVisibleTasks = filteredTasks.length > 0;
    const isWaitingForInitialTasks = isInitialLoad && !hasVisibleTasks;
    const isUpdatingExistingTasks = isTaskListRefreshing && hasVisibleTasks && !isInitialLoad;
    const isTaskListBusy = isWaitingForInitialTasks || isUpdatingExistingTasks;

    return (
      <Collapsible 
        open={!isPanelCollapsed}
        onOpenChange={(open) => {
          if (!mobile) {
            setIsCollapsed(!open);
          }
        }}
        className="h-full"
      >
        <div className={cn(
          "bg-white border-r border-gray-200 flex flex-col h-full transition-all duration-300",
          mobile ? "w-full" : isPanelCollapsed ? "w-12" : "w-80"
        )}>
          { }
          <div
            className={cn(
              "bg-white px-4 py-4 h-20 flex items-center",
              isPanelCollapsed && "justify-center px-0"
            )}
          >
            <div className={cn(
              "flex items-center w-full",
              isPanelCollapsed ? "justify-center" : "justify-between"
            )}>
              {!isPanelCollapsed && (
                <div className="flex items-center gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-2xl font-bold text-gray-900">
                        {showAllTasks ? 'Todas las Tareas' : 'Mis Tareas'}
                      </h2>
                      <output className={cn(
                        "inline-block w-2 h-2 rounded-full",
                        isConnected && !realtimeError ? "bg-green-500" : "bg-red-500"
                      )} aria-live="polite" aria-label={
                        isConnected && !realtimeError ? "Sincronizacion activa" : "Sincronizacion no disponible"
                      } />
                    </div>
                    <p className="text-sm text-gray-600 mt-1 flex items-center">
                      {isWaitingForInitialTasks
                        ? 'Cargando tareas...'
                        : `${filteredTasks.length} ${filteredTasks.length === 1 ? 'tarea' : 'tareas'}${isUpdatingExistingTasks ? ' - actualizando...' : ''}`}
                      <Switch
                        id="task-mode"
                        checked={showAllTasks}
                        onCheckedChange={handleTaskModeChange}
                        className="ml-2"
                      />
                    </p>
                    {error && (
                      <p className="text-xs text-red-600 mt-1">
                        Error: {error}
                      </p>
                    )}
                  </div>
                </div>
              )}
              
              <div className={cn(
                "flex items-center gap-2",
                !isPanelCollapsed && "ml-auto"
              )}>
                {!isPanelCollapsed && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => void loadTasks()}
                    disabled={isTaskListRefreshing}
                    className="p-1"
                  >
                    {isTaskListBusy ? <Spinner /> : <RefreshCw className="h-4 w-4" />}
                  </Button>
                )}

                {mobile && onMobileClose ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onMobileClose}
                    className="p-1"
                    aria-label="Cerrar panel de tareas"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                ) : null}
                
                {!mobile ? (
                  <CollapsibleTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="p-1"
                    >
                      {isPanelCollapsed ? (
                        <ChevronRight className="h-4 w-4" />
                      ) : (
                        <ChevronLeft className="h-4 w-4" />
                      )}
                    </Button>
                  </CollapsibleTrigger>
                ) : null}
              </div>
            </div>
          </div>
          
          <Separator className="h-px bg-gray-200" />
    
          <CollapsibleContent className="flex-1 overflow-hidden data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up">
            <ScrollArea className="h-full" aria-busy={isTaskListBusy}>
              {isWaitingForInitialTasks ? (
                <TaskCardSkeleton fillContainer />
              ) : filteredTasks.length === 0 ? (
                <Empty className="mx-3 my-4 min-h-28 gap-0 border border-gray-200 bg-gray-50 px-4 py-5 md:p-5">
                  <EmptyHeader className="gap-1">
                    <EmptyMedia className="mb-2">
                      <ClipboardList className="size-5 text-gray-400" aria-hidden="true" />
                    </EmptyMedia>
                    <EmptyTitle className="text-sm font-medium tracking-normal text-gray-700">
                      No hay tareas disponibles
                    </EmptyTitle>
                  </EmptyHeader>
                </Empty>
              ) : (
                <div>
                  {filteredTasks.map((task, index) => (
                    <TaskCard
                      key={task.id}
                      task={task}
                      titulo={titulosByProcessInstanceId.get(task.processInstanceId) ?? null}
                      currentUserId={currentUserId || undefined}
                      onClick={onTaskClick}
                      onPrefetch={onTaskPrefetch}
                      showSeparator={index < filteredTasks.length - 1}
                    />
                  ))}
                </div>
              )}
            </ScrollArea>
          </CollapsibleContent>
          
          {isPanelCollapsed ? (
            <button
              type="button"
              aria-label="Expandir panel de tareas"
              className="flex flex-1 cursor-pointer items-center justify-center border-0 bg-transparent p-0"
              onClick={expandCollapsedPanel}
            >
              <div className="text-xs text-gray-500 transform -rotate-90 whitespace-nowrap">
                {filteredTasks.length}
              </div>
            </button>
          ) : null}
        </div>
      </Collapsible>
    );
}
